/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable, signal } from '@angular/core';
import { IErrorNotification } from '@azavista/planner-lib';
import { getApiError, onRequestRejectedDefault } from '@azavista/planner-lib/lib/core/http';
import { IResponseBaseFields, ResponseStatus } from '@azavista/planner-lib/lib/onsite-checkin/print-server-api';
import { AxiosError } from 'axios';
import { TRANSLATION_MAP } from '../../const';
import { TranslateService } from '@ngx-translate/core';

export type OnsiteCheckInClientSingleError = {
    code?: string;
    message?: string;
};

export type OnsiteCheckInClientError = IErrorNotification & OnsiteCheckInClientSingleError

@Injectable({
    providedIn: 'root',
})
export class OnsiteCheckInClientErrorService {
    private translateSvc = inject(TranslateService);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private generalError$ = signal<any | undefined>(undefined);
    private axiosError$ = signal<AxiosError | undefined>(undefined);
    errorNotification$ = signal<IErrorNotification | undefined>(undefined);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setError(error: any, customData?: Partial<OnsiteCheckInClientError>, skipNotification = false) {
        if ((error as AxiosError)?.isAxiosError) {
            this.axiosError$.set(error);
        } else {
            this.generalError$.set(error);
        }
        const formattedError = this.formatError(error, customData);
        if (!skipNotification) {
            this.errorNotification$.set(formattedError);
        }
        console.groupCollapsed(this.getErrorText(formattedError))
        console.error(error.error || formattedError);
        console.groupEnd();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private formatError(error: any, customData?: Partial<OnsiteCheckInClientError>): OnsiteCheckInClientError {
        const apiError = (error as AxiosError).isAxiosError ? getApiError(error) : null;
        return {
            body: error.body || '',
            error,
            method: '',
            url: '',
            caller: '',
            message: apiError?.message || error.message,
            ...error,
            ...customData || {},
        }
    }

    isApiAuthenticationError(err: OnsiteCheckInClientError): boolean {
        const errCode = err?.error?.communicationError?.body?.code;
        if (errCode === 'CouldNotAuthenticate') {
            return true;
        }
        return false;
    }

    getErrorText(err: OnsiteCheckInClientError, customErrorOptions?: {
        customErrorByErrorCodeFn?: (error: OnsiteCheckInClientSingleError) => string;
    }): string {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        err.error ||= err as any;
        const { customErrorByErrorCodeFn } = customErrorOptions || {};
        if (
            err.error &&
            err.error.communicationError &&
            err.error.communicationError.body
        ) {
            const errBody = err.error.communicationError.body;
            // TODO: Change IErrorNotification so it contains code and message
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const errBodyAny = errBody as any;
            if (errBody.error || errBody.error_description) {
                return errBody.error_description + ` (${errBody.error})`;
            } else if (errBodyAny.code || errBodyAny.message) {
                let bodyMsg =
                    (errBodyAny.message as string) +
                    ` (${errBodyAny.code as string})`;
                if (errBodyAny.data && errBodyAny.data.errors) {
                    const errorsArr = errBodyAny.data.errors as string[];
                    bodyMsg += ' ' + errorsArr.join('; ');
                }
                return bodyMsg;
            }
        }
        let msg = `${err.caller || ''} ${err.method || ''} ${err.message || ''} ${
            err.url || ''
        }`.trim();
        const commErr = err.error.communicationError;
        if (commErr) {
            msg += ' ' + commErr.statusText + ' ' + commErr.message;
        }
        const thrownErr = err.error.thrownError;
        if (thrownErr) {
            msg += ' ' + (thrownErr.toString() as string);
        }
        const errorResponse = (err.error as any as AxiosError)?.response?.data as any;
        const errorResponseCode = errorResponse?.code || err.code;
        const errorResponseMessage = errorResponse?.message || err.message;
        const errorCustomMessage = errorResponseCode && customErrorByErrorCodeFn?.({
            code: errorResponseCode,
            message: errorResponseMessage
        }) || errorResponseMessage;
        if (errorCustomMessage) {
            msg = errorCustomMessage;
        }
        const errorArrayFromResponse = Object.values((errorResponse)?.data?.errors || {})
        if (errorArrayFromResponse.length > 0) {
            msg = `${errorCustomMessage ? `${errorCustomMessage}\n` : ''}${errorArrayFromResponse.join(`\n`)}`;
        }
        return msg.trim();
    }

    // Always included errorMsg in the response
    getFormattedPrinterServerErrorResponse<T extends IResponseBaseFields>(
        error: unknown,
        base: Partial<Omit<T, 'status' | 'errorMsg'>> = {}
    ): T {
        const errorAxios = error as AxiosError;
        let errorMessage = this.getErrorText(error as any);
        if (errorAxios.isAxiosError && errorAxios.code === 'ERR_NETWORK') {
            errorMessage = this.translateSvc.instant(TRANSLATION_MAP.PRINT_SERVER_NOT_CONNECTED);
        }
        return {
            ...(base as T),
            execution_time: base.execution_time ?? 0,
            status: ResponseStatus.error,
            errorMsg: errorMessage,
        };
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const axiosErrorHandler = (error: any, errorSvc: OnsiteCheckInClientErrorService, skipNotification = false) => {
    errorSvc.setError(error, null, skipNotification);
    return onRequestRejectedDefault(error);
}
